import {
    getANMS,
    getAuthToken,
    getBidderEmail,
    getBidderId,
    getUserFullName,
    isCookieAuthTokenMismatched,
    isProfileComplete,
    isUserPartiallyRegistered,
    logOutBidder,
} from '@/redux/modules/user';
import { getBuildNumber, getLogRocketAppId } from '@/redux/modules/config';
import { getIsBidderCardValid } from '@/redux/modules/bidderCardStatus';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useEffect, useState } from 'react';
import Cookies from 'universal-cookie';
import LogRocketLib from 'logrocket';

/**
 * Initialize LogRocket client and identify the logged-in user
 * @see https://docs.logrocket.com/docs/getting-started
 */
const LogRocket = () => {
    const dispatch = useAppDispatch();
    const [startLogging, setStartLogging] = useState(false);

    // Get data to identify user
    const ANMS = useAppSelector(getANMS);
    const bidderId = useAppSelector(getBidderId);
    const creditCardValidated = useAppSelector(getIsBidderCardValid);
    const email = useAppSelector(getBidderEmail);
    const fullName = useAppSelector(getUserFullName);
    const profileComplete = useAppSelector(isProfileComplete);
    const partiallyRegistered = useAppSelector(isUserPartiallyRegistered);
    const hasMismatchedAuthCookie = useAppSelector(isCookieAuthTokenMismatched);
    const buildNumber = useAppSelector(getBuildNumber);

    // Validate token
    const reduxAuthToken = useAppSelector(getAuthToken);

    // Key to connect to LogRocket
    const logRocketAppId = useAppSelector(getLogRocketAppId);

    /**
     * Set flag to initialize LogRocket to start recording session
     */
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;

        // Don't init LogRocket in local dev, begin recording session after 30 seconds
        if (!__DEVELOPMENT__) {
            // HOTFIX for ER: if tokens are mismatched, start recording logrocket immediately
            if (hasMismatchedAuthCookie) {
                setStartLogging(true);
            } else {
                if (!timer) {
                    timer = setTimeout(() => {
                        setStartLogging(true);
                    }, 30000);
                }
            }
        }

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initialize LogRocket
    useEffect(() => {
        if (startLogging && bidderId > 0) {
            LogRocketLib.init(logRocketAppId, {
                release: buildNumber,
            });
        }
    }, [startLogging, bidderId, logRocketAppId, buildNumber]);

    // Identify User
    useEffect(() => {
        if (startLogging && bidderId > 0) {
            LogRocketLib.identify(bidderId.toString(), {
                ANMS,
                creditCardValidated,
                email,
                name: fullName,
                profileComplete,
            });
        }
    }, [ANMS, bidderId, creditCardValidated, email, fullName, logRocketAppId, profileComplete, startLogging]);

    // Log message to logrocket if auth tokens exist and are mismatched, then logout user
    useEffect(() => {
        if (hasMismatchedAuthCookie && !partiallyRegistered && !__DEVELOPMENT__) {
            const cookie = new Cookies();
            const cookieAuthToken = cookie.get('bidder-auth');

            LogRocketLib.captureMessage('<< TOKEN MISMATCH >>', {
                extra: {
                    bidderId,
                    cookieAuthToken,
                    currentUrl: window.location.href,
                    reduxAuthToken,
                },
            });

            dispatch(logOutBidder());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
};

export default LogRocket;
